import React from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, BarChart, Clock, LineChart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SleepWindowsChart } from '../dashboard/SleepWindowsChart';
import { SleepDurationHistogram } from '../dashboard/SleepDurationHistogram';
import { SleepWindowsData, SleepDurationData } from '@/types/activity';

// Sample data for the charts
const windowsData: SleepWindowsData[] = [
  { hour_label: '6 PM', sleep_start_count: 2, sleep_end_count: 0 },
  { hour_label: '7 PM', sleep_start_count: 8, sleep_end_count: 0 },
  { hour_label: '8 PM', sleep_start_count: 14, sleep_end_count: 0 },
  { hour_label: '9 PM', sleep_start_count: 5, sleep_end_count: 0 },
  { hour_label: '10 PM', sleep_start_count: 1, sleep_end_count: 0 },
  { hour_label: '5 AM', sleep_start_count: 0, sleep_end_count: 2 },
  { hour_label: '6 AM', sleep_start_count: 0, sleep_end_count: 7 },
  { hour_label: '7 AM', sleep_start_count: 0, sleep_end_count: 13 },
  { hour_label: '8 AM', sleep_start_count: 0, sleep_end_count: 8 }
];

const durationData: SleepDurationData[] = [
  { duration: '7-8 hrs', count: 3 },
  { duration: '8-9 hrs', count: 8 },
  { duration: '9-10 hrs', count: 15 },
  { duration: '10-11 hrs', count: 6 },
  { duration: '11-12 hrs', count: 2 }
];

export function FeatureSection() {
  return (
    <section className="w-full py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container px-4 mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-800">
            Instant Sleep Insights—So You Can Help Parents Faster
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-lg">
            SleepNest transforms how sleep specialists help families by providing clear data visualization and AI-powered insights.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard 
                icon={<Check className="h-6 w-6 text-green-500" />}
                title="One-Tap Tracking for Parents"
                description="Parents can log sleep patterns with a single tap, making consistent tracking effortless."
              />
              <FeatureCard 
                icon={<Zap className="h-6 w-6 text-amber-500" />}
                title="Instant AI-Powered Highlights"
                description="Our AI analyzes patterns to surface the most relevant insights without manual review."
              />
              <FeatureCard 
                icon={<BarChart className="h-6 w-6 text-blue-500" />}
                title="Personalized Sleep Reports"
                description="Generate comprehensive reports tailored to each child's unique sleep journey."
              />
              <FeatureCard 
                icon={<Clock className="h-6 w-6 text-purple-500" />}
                title="Less Admin, More Impact"
                description="Spend less time on paperwork and more time making meaningful interventions."
              />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-purple-100 rounded-full filter blur-xl opacity-70"></div>
            <div className="absolute -bottom-10 -right-6 w-32 h-32 bg-indigo-100 rounded-full filter blur-xl opacity-70"></div>
            
            <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-5 overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-b from-purple-100 to-indigo-100 rounded-bl-full"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">AI Sleep Analysis Dashboard</h3>
                    <p className="text-sm text-gray-500">Powered by advanced pattern recognition</p>
                  </div>
                  <div className="flex space-x-2">
                    <LineChart className="h-5 w-5 text-indigo-500" />
                    <span className="text-sm font-medium text-indigo-600">Sleep Trends</span>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div className="sleep-windows-chart">
                    <SleepWindowsChart data={windowsData} />
                  </div>
                  
                  <div className="sleep-duration-chart">
                    <SleepDurationHistogram data={durationData} />
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <Zap className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-indigo-900">AI Insight</h4>
                        <p className="text-xs text-indigo-700">Child consistently falls asleep between 8-9 PM, suggesting an optimal bedtime window.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <motion.div 
      whileHover={{ y: -5 }}
      className={cn(
        "p-6 rounded-xl bg-white border border-gray-100 shadow-sm",
        "transition-all duration-200"
      )}
    >
      <div className="mb-4">{icon}</div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </motion.div>
  );
}